import { Controller } from "stimulus"

export default class extends Controller {
  static values = {
    noResults: String,
    searchPlaceholder: String,
    revealSelector: String,
    revealWhen: String
  }

  connect() {
    this.multiple = this.element.multiple
    this.active = null
    this.query = ""
    this.onOutside = (event) => {
      if (!this.root || !this.root.contains(event.target)) this.close()
    }
    this.onSelectFocus = () => {
      if (this.button) this.button.focus()
    }

    this.build()
    this.syncReveal()
    this.element.addEventListener("focus", this.onSelectFocus)
    document.addEventListener("click", this.onOutside)
  }

  disconnect() {
    document.removeEventListener("click", this.onOutside)
    this.element.removeEventListener("focus", this.onSelectFocus)
    if (this.labelFor && this.element.id) {
      this.labelFor.setAttribute("for", this.element.id)
    }
    if (this.root && this.root.parentNode) {
      this.root.parentNode.removeChild(this.root)
    }
    this.element.classList.remove("select-picker-native")
    this.element.removeAttribute("tabindex")
    this.element.removeAttribute("aria-hidden")
    this.root = null
  }

  build() {
    // Keep the <select> in place: moving it would reconnect Stimulus forever.
    this.element.classList.add("select-picker-native")
    this.element.setAttribute("tabindex", "-1")
    this.element.setAttribute("aria-hidden", "true")

    this.root = document.createElement("div")
    this.root.className = "select-picker"
    this.element.insertAdjacentElement("afterend", this.root)

    this.button = document.createElement("button")
    this.button.type = "button"
    this.button.className = "select-picker-button"
    this.button.setAttribute("aria-haspopup", "listbox")
    this.button.setAttribute("aria-expanded", "false")
    if (this.element.id) {
      this.button.id = `${this.element.id}_picker`
      this.labelFor = document.querySelector(`label[for="${this.element.id}"]`)
      if (this.labelFor) this.labelFor.setAttribute("for", this.button.id)
    }
    if (this.element.disabled) this.button.disabled = true
    this.button.addEventListener("click", (event) => this.toggle(event))

    this.label = document.createElement("span")
    this.label.className = "select-picker-label"
    this.button.appendChild(this.label)

    this.chevron = document.createElement("span")
    this.chevron.className = "select-picker-chevron"
    this.chevron.setAttribute("aria-hidden", "true")
    this.chevron.innerHTML = '<i class="fas fa-chevron-down"></i>'
    this.button.appendChild(this.chevron)

    this.menu = document.createElement("div")
    this.menu.className = "select-picker-menu"
    this.menu.hidden = true

    const searchWrap = document.createElement("div")
    searchWrap.className = "select-picker-search"
    this.search = document.createElement("input")
    this.search.type = "search"
    this.search.className = "form-control"
    this.search.autocomplete = "off"
    this.search.spellcheck = false
    this.search.placeholder = this.hasSearchPlaceholderValue
      ? this.searchPlaceholderValue
      : (this.element.dataset.placeholder || "")
    this.search.setAttribute("aria-autocomplete", "list")
    this.search.addEventListener("input", () => this.filter())
    searchWrap.appendChild(this.search)

    this.list = document.createElement("div")
    this.list.className = "select-picker-list"
    this.list.setAttribute("role", "listbox")
    if (this.multiple) this.list.setAttribute("aria-multiselectable", "true")
    this.list.addEventListener("click", (event) => this.choose(event))

    this.status = document.createElement("p")
    this.status.className = "select-picker-status"
    this.status.hidden = true

    this.menu.appendChild(searchWrap)
    this.menu.appendChild(this.list)
    this.menu.appendChild(this.status)

    this.root.appendChild(this.button)
    this.root.appendChild(this.menu)
    this.root.addEventListener("keydown", (event) => this.keydown(event))


    this.renderOptions()
    this.refreshLabel()
  }

  toggle(event) {
    event.preventDefault()
    if (this.element.disabled) return
    this.menu.hidden ? this.open() : this.close()
  }

  open() {
    this.menu.hidden = false
    this.button.setAttribute("aria-expanded", "true")
    this.root.classList.add("is-open")
    this.query = ""
    this.search.value = ""
    this.renderOptions()
    this.search.focus()
    const selected = this.optionButtons().find((button) => button.getAttribute("aria-selected") === "true")
    this.highlight(selected || this.optionButtons()[0])
  }

  close() {
    if (!this.menu || this.menu.hidden) return
    this.menu.hidden = true
    this.button.setAttribute("aria-expanded", "false")
    this.root.classList.remove("is-open")
    this.active = null
  }

  filter() {
    this.query = this.search.value.trim().toLowerCase()
    this.renderOptions()
    this.highlight(this.optionButtons()[0])
  }

  keydown(event) {
    if (!this.menu || this.menu.hidden) {
      if (event.key === "ArrowDown" || event.key === "Enter" || event.key === " ") {
        if (event.target === this.button) {
          event.preventDefault()
          this.open()
        }
      }
      return
    }

    if (event.key === "Escape") {
      event.preventDefault()
      this.close()
      this.button.focus()
    } else if (event.key === "ArrowDown") {
      event.preventDefault()
      this.move(1)
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      this.move(-1)
    } else if (event.key === "Enter") {
      event.preventDefault()
      if (this.active) this.selectOption(this.active)
    }
  }

  choose(event) {
    const option = event.target.closest("[data-select-value]")
    if (!option || !this.list.contains(option)) return
    this.selectOption(option)
  }

  selectOption(option) {
    const value = option.dataset.selectValue

    if (this.multiple) {
      const nativeOption = this.findNativeOption(value)
      if (!nativeOption) return
      nativeOption.selected = !nativeOption.selected
      this.renderOptions()
      this.refreshLabel()
      this.dispatchChange()
      this.highlight(option)
      return
    }

    this.element.value = value
    Array.from(this.element.options).forEach((nativeOption) => {
      nativeOption.selected = nativeOption.value === value
    })
    this.refreshLabel()
    this.dispatchChange()
    this.close()
    this.button.focus()
  }

  renderOptions() {
    this.list.innerHTML = ""
    const options = Array.from(this.element.options)
    let visibleCount = 0

    options.forEach((nativeOption) => {
      if (this.multiple && nativeOption.value === "") return

      const label = nativeOption.textContent.trim()
      const value = nativeOption.value
      if (this.query && label.toLowerCase().indexOf(this.query) === -1) return

      visibleCount += 1
      const button = document.createElement("button")
      button.type = "button"
      button.className = "select-picker-option"
      button.dataset.selectValue = value
      button.setAttribute("role", "option")
      button.setAttribute("aria-selected", nativeOption.selected ? "true" : "false")
      if (nativeOption.selected) button.classList.add("is-selected")

      const caption = document.createElement("span")
      caption.className = "select-picker-option-label"
      caption.textContent = label || this.blankLabel()
      button.appendChild(caption)

      if (this.multiple) {
        const check = document.createElement("span")
        check.className = "select-picker-check"
        check.setAttribute("aria-hidden", "true")
        check.innerHTML = '<i class="fas fa-check"></i>'
        button.appendChild(check)
      }

      this.list.appendChild(button)
    })

    const emptyMessage = this.hasNoResultsValue ? this.noResultsValue : "Aucun résultat"
    this.status.textContent = emptyMessage
    this.status.hidden = visibleCount > 0
  }

  refreshLabel() {
    if (this.multiple) {
      const selected = Array.from(this.element.selectedOptions).filter((option) => option.value !== "")
      this.button.classList.toggle("has-value", selected.length > 0)
      this.label.classList.add("is-multiple")
      this.label.innerHTML = ""

      if (selected.length === 0) {
        this.label.textContent = this.placeholder()
        this.label.classList.add("is-placeholder")
        return
      }

      this.label.classList.remove("is-placeholder")
      selected.forEach((option) => {
        const chip = document.createElement("span")
        chip.className = "select-picker-chip"
        chip.textContent = option.textContent.trim()

        const remove = document.createElement("button")
        remove.type = "button"
        remove.className = "select-picker-chip-remove"
        remove.setAttribute("aria-label", "Remove")
        remove.innerHTML = '<i class="fas fa-times"></i>'
        remove.addEventListener("click", (event) => {
          event.preventDefault()
          event.stopPropagation()
          option.selected = false
          this.renderOptions()
          this.refreshLabel()
          this.dispatchChange()
        })
        chip.appendChild(remove)
        this.label.appendChild(chip)
      })
      return
    }

    this.label.classList.remove("is-multiple")
    const selected = this.element.options[this.element.selectedIndex]
    const hasValue = selected && selected.value !== ""
    this.button.classList.toggle("has-value", Boolean(hasValue))
    this.label.classList.toggle("is-placeholder", !hasValue)
    this.label.textContent = hasValue
      ? selected.textContent.trim()
      : (selected && selected.textContent.trim()) || this.placeholder()
  }

  placeholder() {
    return this.element.dataset.placeholder || this.blankLabel() || ""
  }

  blankLabel() {
    const blank = Array.from(this.element.options).find((option) => option.value === "")
    return blank ? blank.textContent.trim() : ""
  }

  findNativeOption(value) {
    return Array.from(this.element.options).find((option) => option.value === value)
  }

  optionButtons() {
    return Array.from(this.list.querySelectorAll("[data-select-value]"))
  }

  move(delta) {
    const items = this.optionButtons()
    if (items.length === 0) return
    const index = items.indexOf(this.active)
    const next = items[(index + delta + items.length) % items.length]
    this.highlight(next)
  }

  highlight(item) {
    if (!item) return
    if (this.active) this.active.classList.remove("is-active")
    this.active = item
    item.classList.add("is-active")
    item.scrollIntoView({ block: "nearest" })
  }

  dispatchChange() {
    this.element.dispatchEvent(new Event("change", { bubbles: true }))
    this.syncReveal()
  }

  syncReveal() {
    if (!this.hasRevealSelectorValue) return
    const scope = this.element.closest("form") || document
    const target = scope.querySelector(this.revealSelectorValue)
    if (!target) return
    const shouldShow = this.hasRevealWhenValue
      ? this.element.value === this.revealWhenValue
      : Boolean(this.element.value)
    target.style.display = shouldShow ? "" : "none"
  }
}
